import { AfterViewInit, Component, EventEmitter, Input, Output } from '@angular/core';
import { libraryImages } from '@images/library-images';
/**
 * Type-only import — erased at compile time, so it does NOT pull the package into this chunk.
 * The real module is loaded via `await import(...)` in `_createImageEditor`, see the note there.
 */
import type ImageEditor from 'tui-image-editor';

@Component({
  selector: 'app-lib-image-editor',
  templateUrl: './lib-image-editor.component.html',
  styleUrls: ['./lib-image-editor.component.scss'],
})
export class LibImageEditorComponent implements AfterViewInit {
  @Input() src: any;
  @Input() watermarks: boolean = false;
  @Output() BeforeLoading: EventEmitter<any> = new EventEmitter();
  @Output() AfterLoading: EventEmitter<any> = new EventEmitter();
  @Output() LoadingError: EventEmitter<any> = new EventEmitter();
  @Output() imageEdited: EventEmitter<any> = new EventEmitter();

  public _tuiImageEditor!: ImageEditor;
  private hasChanges: boolean = false;
  images = libraryImages;

  public ngAfterViewInit() {
    this._createImageEditor();
  }

  myTheme = {
    // custom icons
    'menu.normalIcon.path': this.images?.img_editor_d,
    'menu.normalIcon.name': 'icon-d',
    'menu.activeIcon.path': this.images?.img_editor_b,
    'menu.activeIcon.name': 'icon-b',
    'menu.disabledIcon.path': this.images?.img_editor_a,
    'menu.disabledIcon.name': 'icon-a',
    'menu.hoverIcon.path': this.images?.img_editor_c,
    'menu.hoverIcon.name': 'icon-c',
    'submenu.normalIcon.path': this.images?.img_editor_a,
    'submenu.normalIcon.name': 'icon-a',
    'submenu.activeIcon.path': this.images?.img_editor_c,
    'submenu.activeIcon.name': 'icon-c',

    // common
    'common.bi.image': 'https://uicdn.toast.com/toastui/img/tui-image-editor-bi.png',
    'common.bisize.width': '251px',
    'common.bisize.height': '21px',
    'common.backgroundImage': './img/bg.png',
    'common.backgroundColor': '#fff',
    'common.border': '1px solid #c1c1c1',

    // header
    'header.backgroundImage': 'none',
    'header.backgroundColor': 'transparent',
    'header.border': '0px',

    // load button
    'loadButton.backgroundColor': '#fff',
    'loadButton.border': '1px solid #ddd',
    'loadButton.color': '#222',
    'loadButton.fontFamily': "'Noto Sans', sans-serif",
    'loadButton.fontSize': '12px',

    // download button
    'downloadButton.backgroundColor': '#fdba3b',
    'downloadButton.border': '1px solid #229276',
    'downloadButton.color': '#fff',
    'downloadButton.fontFamily': "'Noto Sans', sans-serif",
    'downloadButton.fontSize': '12px',

    // main icons
    'menu.normalIcon.color': '#1a2f45 ',
    'menu.activeIcon.color': '#229276',
    'menu.disabledIcon.color': '#434343',
    'menu.hoverIcon.color': '#229276',
    'menu.iconSize.width': '24px',
    'menu.iconSize.height': '24px',

    // submenu icons
    'submenu.normalIcon.color': '#1a2f45 ',
    'submenu.activeIcon.color': '#229276',
    'submenu.iconSize.width': '24px',
    'submenu.iconSize.height': '24px',

    // submenu primary color
    'submenu.backgroundColor': 'transparent',
    'submenu.partition.color': '#e5e5e5',

    // submenu labels
    'submenu.normalLabel.color': '#858585',
    'submenu.normalLabel.fontWeight': 'normal',
    'submenu.activeLabel.color': '#229276',
    'submenu.activeLabel.fontWeight': 'normal',

    // checkbox style
    'checkbox.border': '1px solid #ccc',
    'checkbox.backgroundColor': '#fff',

    // range style
    'range.pointer.color': '#229276',
    'range.bar.color': '#f5f5f5',
    'range.subbar.color': '#229276',

    'range.disabledPointer.color': '#d3d3d3',
    'range.disabledBar.color': 'rgba(85,85,85,0.06)',
    'range.disabledSubbar.color': 'rgba(51,51,51,0.2)',

    'range.value.color': '#000',
    'range.value.fontWeight': 'normal',
    'range.value.fontSize': '11px',
    'range.value.border': '0',
    'range.value.backgroundColor': '#f5f5f5',
    'range.title.color': '#000',
    'range.title.fontWeight': 'lighter',

    // colorpicker style
    'colorpicker.button.border': '0px',
    'colorpicker.title.color': '#000',
  };
  whiteTheme = {
    // common
    'common.bi.image': 'https://uicdn.toast.com/toastui/img/tui-image-editor-bi.png',
    'common.bisize.width': '251px',
    'common.bisize.height': '21px',
    'common.backgroundImage': './img/bg.png',
    'common.backgroundColor': '#fff',
    'common.border': '1px solid #c1c1c1',

    // header
    'header.backgroundImage': 'none',
    'header.backgroundColor': 'transparent',
    'header.border': '0px',

    // load button
    'loadButton.backgroundColor': '#fff',
    'loadButton.border': '1px solid #ddd',
    'loadButton.color': '#222',
    'loadButton.fontFamily': "'Noto Sans', sans-serif",
    'loadButton.fontSize': '12px',

    // download button
    'downloadButton.backgroundColor': '#fdba3b',
    'downloadButton.border': '1px solid #229276',
    'downloadButton.color': '#fff',
    'downloadButton.fontFamily': "'Noto Sans', sans-serif",
    'downloadButton.fontSize': '12px',

    // main icons
    'menu.normalIcon.color': '#1a2f45 ',
    'menu.activeIcon.color': '#555555',
    'menu.disabledIcon.color': '#434343',
    'menu.hoverIcon.color': '#e9e9e9',
    'menu.iconSize.width': '24px',
    'menu.iconSize.height': '24px',

    // submenu icons
    'submenu.normalIcon.color': '#1a2f45 ',
    'submenu.activeIcon.color': '#555555',
    'submenu.iconSize.width': '32px',
    'submenu.iconSize.height': '32px',

    // submenu primary color
    'submenu.backgroundColor': 'transparent',
    'submenu.partition.color': '#e5e5e5',

    // submenu labels
    'submenu.normalLabel.color': '#858585',
    'submenu.normalLabel.fontWeight': 'normal',
    'submenu.activeLabel.color': '#000',
    'submenu.activeLabel.fontWeight': 'normal',

    // checkbox style
    'checkbox.border': '1px solid #ccc',
    'checkbox.backgroundColor': '#fff',

    // range style
    'range.pointer.color': '#333',
    'range.bar.color': '#ccc',
    'range.subbar.color': '#606060',

    'range.disabledPointer.color': '#d3d3d3',
    'range.disabledBar.color': 'rgba(85,85,85,0.06)',
    'range.disabledSubbar.color': 'rgba(51,51,51,0.2)',

    'range.value.color': '#000',
    'range.value.fontWeight': 'normal',
    'range.value.fontSize': '11px',
    'range.value.border': '0',
    'range.value.backgroundColor': '#f5f5f5',
    'range.title.color': '#000',
    'range.title.fontWeight': 'lighter',

    // colorpicker style
    'colorpicker.button.border': '0px',
    'colorpicker.title.color': '#000',
  };

  blackTheme = {
    // common
    'common.bi.image': 'https://uicdn.toast.com/toastui/img/tui-image-editor-bi.png',
    'common.bisize.width': '251px',
    'common.bisize.height': '21px',
    'common.backgroundImage': 'none',
    'common.backgroundColor': '#1e1e1e',
    'common.border': '0px',

    // header
    'header.backgroundImage': 'none',
    'header.backgroundColor': 'transparent',
    'header.border': '0px',

    // load button
    'loadButton.backgroundColor': '#fff',
    'loadButton.border': '1px solid #ddd',
    'loadButton.color': '#222',
    'loadButton.fontFamily': "'Noto Sans', sans-serif",
    'loadButton.fontSize': '12px',

    // download button
    'downloadButton.backgroundColor': '#fdba3b',
    'downloadButton.border': '1px solid #229276',
    'downloadButton.color': '#fff',
    'downloadButton.fontFamily': "'Noto Sans', sans-serif",
    'downloadButton.fontSize': '12px',

    // checkbox style
    'checkbox.border': '0px',
    'checkbox.backgroundColor': '#fff',

    // range style
    'range.pointer.color': '#fff',
    'range.bar.color': '#666',
    'range.subbar.color': '#d1d1d1',

    'range.disabledPointer.color': '#414141',
    'range.disabledBar.color': '#282828',
    'range.disabledSubbar.color': '#414141',

    'range.value.color': '#fff',
    'range.value.fontWeight': 'lighter',
    'range.value.fontSize': '11px',
    'range.value.border': '1px solid #353535',
    'range.value.backgroundColor': '#151515',
    'range.title.color': '#fff',
    'range.title.fontWeight': 'lighter',

    // colorpicker style
    'colorpicker.button.border': '1px solid #1e1e1e',
    'colorpicker.title.color': '#fff',
  };

  // Loads an image into the TUI Image Editor from a given URL and emits events before and after loading.
  public loadImage(path) {
    this.BeforeLoading.emit(); // Emit event before the image starts loading

    this._tuiImageEditor.loadImageFromURL(path, 'My sample image').then(() => {
      this.AfterLoading.emit(); // Emit event after the image has successfully loaded
    });
  }

  /**
   * Conserve aspect ratio of the original region. Useful when shrinking/enlarging
   * images to fit into a certain area.
   *
   * @param {Number} srcWidth Source area width
   * @param {Number} srcHeight Source area height
   * @param {Number} maxWidth Fittable area maximum available width
   * @param {Number} maxHeight Fittable area maximum available height
   * @return {Object} { width, height }
   */
  calculateAspectRatioFit(srcWidth, srcHeight, maxWidth, maxHeight) {
    var ratio = Math.min(maxWidth / srcWidth, maxHeight / srcHeight);
    return { width: srcWidth * ratio, height: srcHeight * ratio };
  }

  public _distroyImageEditor() {
    this._tuiImageEditor.destroy();
    this._tuiImageEditor = null;
  }

  // Initializes the TUI Image Editor with custom options and settings
  /**
   * Loads tui-image-editor on demand.
   *
   * It must NOT be a static import. `tui-image-editor`'s entry (`dist/tui-image-editor.js`)
   * inlines a legacy IE polyfill bundle that runs as an IIFE on module evaluation. Part of it is
   * svg4everybody, which — even with zero `<use>` elements on the page — unconditionally attaches
   * a MutationObserver to `document.documentElement` with `{childList, subtree, attributes}`.
   * Its callback debounces a 100ms `setTimeout` that calls `getBoundingClientRect()` on every
   * `<use>` element (a forced synchronous reflow each time) and then RE-ATTACHES the observer.
   * In an Angular app the DOM never stops mutating, so it never settles: the main thread locks up.
   *
   * `SetupModule` imports `LibraryImageEditorModule`, so a static import put that IIFE in the
   * settings chunk and froze the tab on every `/setting/setup/*` route — nowhere near the editor.
   * Loading it here keeps it out of every chunk until the editor is actually opened.
   */
  public async _createImageEditor() {
    const { default: ImageEditor } = await import('tui-image-editor');

    let options = {
      includeUI: {
        loadImage: {
          // Default image to load in the editor
          path: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAAAAAA6fptVAAAACklEQVQYV2P4DwABAQEAWk1v8QAAAABJRU5ErkJggg==',
          name: 'SampleImage',
        },
        theme: this.myTheme, // Custom theme (e.g., whiteTheme)
        menuBarPosition: 'right', // Positions the menu bar on the right
      },
      selectionStyle: {
        // Custom selection styling for elements in the editor
        cornerStyle: 'dotted',
        cornerSize: 15,
        cornerColor: '#229276',
        cornerStrokeColor: '#229276',
        transparentCorners: false,
        lineWidth: 5,
        borderColor: '#229276',
        rotatingPointOffset: 70,
      },
    };

    // If watermarks are enabled, restrict the menu options
    if (this.watermarks) {
      options.includeUI['menu'] = ['resize', 'crop', 'flip', 'rotate', 'text', 'filter'];
    }

    // Initialize the TUI Image Editor with the specified options
    this._tuiImageEditor = new ImageEditor(document.querySelector('#tui-image-editor'), options);

    // Load the image into the editor and adjust the canvas dimensions
    window.setTimeout(() => {
      this.BeforeLoading.emit(); // Emit event before loading starts
      this._tuiImageEditor
        .loadImageFromURL(this.src, 'My sample image')
        .then(() => {
          this.AfterLoading.emit(); // Emit event after loading is complete

          // Get the current canvas dimensions
          let canvasWidth = this._tuiImageEditor.getCanvasSize().width;
          let canvasHeight = this._tuiImageEditor.getCanvasSize().height;

          // Get the parent container element
          let el: any = window.document.getElementsByClassName('libBodyRight')[0];

          // Calculate new dimensions while maintaining aspect ratio
          let newDim = this.calculateAspectRatioFit(canvasWidth, canvasHeight, el.clientWidth - 250, el.clientHeight / 1.1);

          // Resize the canvas with the new dimensions
          this._tuiImageEditor.resizeCanvasDimension({
            width: newDim.width,
            height: newDim.height,
          });
        })
        .catch(() => {
          this.LoadingError.emit(); // Emit error event if loading fails
        });
    }, 300);

    // Modify UI elements after initialization
    window.setTimeout(() => {
      let el: any = window.document.getElementsByClassName('tie-btn-deleteAll')[0];
      el.setAttribute('tooltip-content', 'Reset'); // Set tooltip for reset button
    }, 300);

    // Remove the delete button
    window.setTimeout(() => {
      let el: any = window.document.getElementsByClassName('tie-btn-delete')[0];
      if (el) {
        el.remove();
      }
    }, 300);

    // Ensure "Lock Aspect Ratio" checkbox is checked by default
    window.setTimeout(() => {
      let el = document.querySelector('.tie-lock-aspect-ratio') as HTMLInputElement;
      if (el) {
        el.checked = true;
        el.dispatchEvent(new Event('change')); // Dispatch change event
      }
    }, 300);

    // Update tooltip content for various UI elements
    window.setTimeout(() => {
      let el: any = window.document.getElementsByClassName('tie-btn-hand')[0];
      el.setAttribute('tooltip-content', 'Move'); // Set tooltip for move button
    }, 300);

    window.setTimeout(() => {
      let el: any = window.document.getElementsByClassName('tie-btn-mask')[0];
      el.setAttribute('tooltip-content', 'Upload Image'); // Set tooltip for upload button
    }, 300);

    // Update text labels for specific UI elements
    window.setTimeout(() => {
      let el = document.querySelector('.tui-image-editor-menu-mask .tui-image-editor-submenu-item .tui-image-editor-button label');
      el.textContent = 'Load Image'; // Rename the load image button
    }, 300);

    window.setTimeout(() => {
      let el = document.querySelector('.tui-image-editor-menu-draw .tui-image-editor-submenu-item .tui-image-editor-newline label');
      el.textContent = 'Thickness'; // Rename thickness label
    }, 300);

    // Adjust the cancel button icon size
    window.setTimeout(() => {
      let el = window.document.querySelector('.tui-image-editor-container .tui-image-editor-button.cancel svg');
      if (el) {
        el.setAttribute('style', 'height: 18px');
      }
    }, 300);

    // Listen for changes in the undo stack to track edits
    this._tuiImageEditor.on('undoStackChanged', length => {
      this.imageEdited.emit(this.hasChanges);
      if (length >= 2) this.hasChanges = true; // Mark changes when undo stack reaches 2
    });
  }
}
